import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";

import * as moment from "moment";

import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/reducers";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "../../services/snackbar/snackbar.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

export const DATEPICKER_FORM = {
  modes: "modes",
  startDate: "startDate",
  endDate: "endDate",
};

@Component({
  template: ''
})
export class BaseDatepicker {
  startDate: Date = new Date();
  endDate: Date = new Date();

  datepickerFormGroup: FormGroup;

  dateMode = new FormControl("");
  selMode: string;

  @Input() selectedMode?: string = "TODAY";
  @Input() hasSiteFilter: boolean = false;
  @Input() siteLength: number = 0;
  @Input() datesClass: string | string[] = [];

  @Output() public mode: EventEmitter<string> = new EventEmitter();
  @Output() public dates: EventEmitter<any> = new EventEmitter();
  @Output() public siteFilter: EventEmitter<any> = new EventEmitter();

  constructor(
    public store: Store<AppState>,
    public fb: FormBuilder,
    public translate: TranslateService,
    public snackbarService: SnackbarService
  ) {}

  createDatePickerForm() {
    this.datepickerFormGroup = this.fb.group({
      [DATEPICKER_FORM.modes]: [null],
      [DATEPICKER_FORM.startDate]: [this.startDate],
      [DATEPICKER_FORM.endDate]: [this.endDate],
    });
  }

  loadDatePicker() {
    this.startDate.setSeconds(0);
    this.startDate.setHours(0);
    this.startDate.setMinutes(0);
  
    this.endDate.setSeconds(59);
    this.endDate.setMinutes(59);
    this.endDate.setHours(23);
  
    this.datepickerFormGroup.controls.startDate.setValue(this.startDate);
    this.datepickerFormGroup.controls.endDate.setValue(this.endDate);
    
    this.calculateDate(this.selectedMode);
  }

  toggleShowSiteFilter() {
    this.siteFilter.emit();
  }

  calculateDate(selMode: string, startingDate?: Date, endingDate?: Date) {
    if (!selMode) {
      this.selMode = null;
      this.mode.emit(null);
      this.dates.emit({ startDate: null, endDate: null });
      return;
    } 

    if (startingDate) {
      this.startDate = startingDate;
    }
    if (endingDate) {
      this.endDate = endingDate;
    }

    switch (selMode) {
      case "TODAY":
        this.startDate = moment().toDate();
        this.endDate = moment().toDate();
        break;

      case "YESTERDAY":
        this.startDate = moment().subtract(1, "day").toDate();
        this.endDate = moment().subtract(1, "day").toDate();
        break;

      case "WEEK":
        this.startDate = moment().subtract(7, "day").toDate();
        this.endDate = moment().toDate();
        break;

      case "MONTH":
        this.startDate = moment().subtract(1, "month").toDate();
        this.endDate = moment().toDate();
        break;

      case "CUSTOM":
        if (this.startDate > this.endDate) {
          this.snackbarService.show({
            message: this.translate.instant("Dates.invalidRange"),
            type: SnackbarType.WARNING,
          });
          return;
        } else {
          this.startDate = moment(this.startDate).toDate();
          this.endDate = moment(this.endDate).toDate();
        }
        break;
      default:
        break;
    }

    if (selMode !== "CUSTOM") {
      this.startDate = new Date(this.startDate);
      this.startDate.setMilliseconds(0);
      this.startDate.setSeconds(0);
      this.startDate.setMinutes(0);
      this.startDate.setHours(0);

      this.endDate = new Date(this.endDate);
      this.endDate.setMilliseconds(999);
      this.endDate.setSeconds(59);
      this.endDate.setMinutes(59);
      this.endDate.setHours(23);
    }

    this.datepickerFormGroup.controls.startDate.setValue(this.startDate);
    this.datepickerFormGroup.controls.endDate.setValue(this.endDate);

    const result = {
      startDate: moment(this.startDate).format('YYYY-MM-DDTHH:mm:ss.SSS'),
      endDate: moment(this.endDate).format('YYYY-MM-DDTHH:mm:ss.SSS'),
    };

    this.selMode = selMode;
    this.mode.emit(selMode)
    this.dates.emit(result);
  }

  closeDatePicker() {
    const startValue = this.datepickerFormGroup.controls.startDate.value;
    const endValue = this.datepickerFormGroup.controls.endDate.value;

    const startDate = startValue ? new Date(startValue._d?.getTime() ?? startValue) : null;
    const endDate = endValue ? new Date(endValue._d?.getTime() ?? endValue) : null;

    this.calculateDate(
      this.datepickerFormGroup.controls[DATEPICKER_FORM.modes].value || "CUSTOM",
      startDate,
      endDate
    );
  }
}
